import React from 'react';
import type { SelectHTMLAttributes } from 'react';
import { Tooltip } from './Tooltip';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  required?: boolean;
  /** Optional contextual help shown as a hover/focus tooltip beside the label */
  hint?: string;
}

export function Select({
  label,
  options,
  placeholder,
  error,
  required,
  hint,
  id,
  ...rest
}: SelectProps): React.ReactElement {
  const selectId = id ?? `select-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className={`input-group${error ? ' input-group--error' : ''}`}>
      <label htmlFor={selectId} className="input-group__label">
        {label}
        {required && <span className="input-group__required" aria-hidden="true">*</span>}
        {hint && (
          <Tooltip content={hint}>
            <span className="help-icon" aria-hidden="true">?</span>
          </Tooltip>
        )}
      </label>
      <select
        id={selectId}
        className="input-group__select"
        aria-invalid={!!error}
        aria-describedby={error ? `${selectId}-error` : undefined}
        required={required}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <span id={`${selectId}-error`} className="input-group__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
